import type { ReactNode } from 'react'
import { OverflowTip } from './Hover/OverflowTip'
import { labelText } from './labelText'
import { useRailPageShellCollapse } from './railPageShellContext'
import { Pressable } from './Pressable'
import './RailChipTabs.css'

const toInitials = (label: ReactNode): string => {
  const raw = labelText(label).trim()
  if (!raw) return '·'
  const parts = raw.split(/[\s_-]+/).filter(Boolean)
  if (parts.length === 0) return raw.charAt(0).toUpperCase()
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
}

export type RailChipTabItem<T extends string> = {
  key: T
  label: ReactNode
  icon?: ReactNode
  subtitle?: ReactNode
  disabled?: boolean
  hidden?: boolean
  count?: number
  testId?: string
  tourId?: string
}

export type RailChipTabsProps<T extends string> = {
  items: RailChipTabItem<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  className?: string
  orientation?: 'vertical' | 'horizontal'
}

const joinClasses = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ')

/**
 * Canonical LCM (UI-MPxCLCM) primitive — chips/tabs for left-rail primary
 * navigation. Vertical by default (matches image 2). Every migrated page's
 * primary nav should render through this component so styling stays uniform.
 *
 * Presentational only — no internal state, no side effects. Consumer owns
 * the active key and change callback.
 */
export function RailChipTabs<T extends string>({
  items,
  value,
  onChange,
  ariaLabel,
  className,
  orientation = 'vertical',
}: RailChipTabsProps<T>) {
  const visible = items.filter((item) => !item.hidden)
  const { collapsed, useInitials } = useRailPageShellCollapse()
  if (visible.length === 0) return null

  return (
    <div
      className={joinClasses(
        'railChipTabs',
        `railChipTabs--${orientation}`,
        collapsed && 'railChipTabs--collapsed',
        className,
      )}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={orientation}
    >
      {visible.map((item) => {
        const isActive = item.key === value
        const collapsedGlyph = useInitials
          ? <span className="railChipTabs__initials">{toInitials(item.label)}</span>
          : item.icon !== undefined
            ? <span className="railChipTabs__icon" aria-hidden="true">{item.icon}</span>
            : <span className="railChipTabs__initials">{toInitials(item.label)}</span>
        const labelStr = labelText(item.label)
        return (
          <Pressable
            key={item.key}
            data-testid={item.testId}
            data-tour-id={item.tourId}
            data-orbit-target="lcm-chip"
            data-lcm-chip-id={item.key}
            data-orbit-title={labelStr}
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled ? true : undefined}
            disabled={item.disabled}
            className={joinClasses(
              'railChipTabs__tab',
              isActive && 'isActive',
            )}
            onClick={() => {
              if (item.disabled || isActive) return
              onChange(item.key)
            }}
            aria-label={collapsed && labelStr ? labelStr : undefined}
            title={collapsed && labelStr ? labelStr : undefined}
          >
            {collapsed
              ? collapsedGlyph
              : item.icon !== undefined && (
                <span className="railChipTabs__icon" aria-hidden="true">{item.icon}</span>
              )}
            <span className="railChipTabs__meta">
              {orientation === 'horizontal' ? (
                <OverflowTip label={labelText(item.label)}>
                  <span className="railChipTabs__label">{item.label}</span>
                </OverflowTip>
              ) : (
                <span className="railChipTabs__label">{item.label}</span>
              )}
              {item.subtitle !== undefined && (
                <span className="railChipTabs__subtitle">{item.subtitle}</span>
              )}
            </span>
            {item.count !== undefined && (
              <span className="railChipTabs__count" aria-hidden="true">{item.count}</span>
            )}
          </Pressable>
        )
      })}
    </div>
  )
}

export default RailChipTabs
